import { useTranslation } from 'react-i18next';
import { track } from '../../lib/analytics';
import { cn } from '../../lib/utils';

export interface Article {
  id: string;
  title: string;
  url: string;
  source_url: string;
  published_at: string | null;
  excerpt: string;
  author?: string;
}

export interface ArticleListPanelProps {
  feedId: string;
  articles: Article[];
  loading: boolean;
  error: boolean;
  /** Human readable sync status, rendered next to the article count. */
  syncLabel: string;
  selectedArticleId: string | null;
  onSelectArticle: (article: Article) => void;
}

function formatRelative(
  iso: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const minutes = Math.floor((Date.now() - then) / 60_000);
  if (minutes < 1) return t('preview.justNow');
  if (minutes < 60) return t('preview.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('preview.hoursAgo', { count: hours });
  return t('preview.daysAgo', { count: Math.floor(hours / 24) });
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function textFromHtml(value: string): string {
  if (!value) return '';
  if (typeof window === 'undefined') {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const element = window.document.createElement('div');
  element.innerHTML = value;
  return (element.textContent ?? element.innerText ?? '').replace(/\s+/g, ' ').trim();
}

function hasSummary(article: Article): boolean {
  return /summary|摘要/i.test(textFromHtml(article.excerpt));
}

export function ArticleListPanel({
  feedId,
  articles,
  loading,
  error,
  syncLabel,
  selectedArticleId,
  onSelectArticle,
}: ArticleListPanelProps) {
  const { t } = useTranslation('feed-builder');

  return (
    <section
      aria-label={t('reader.list.title')}
      className="flex h-full w-[300px] shrink-0 flex-col border-r border-brand-border bg-brand-surface lg:w-[340px]"
    >
      <header className="shrink-0 px-4 pb-2.5 pt-3.5">
        <div className="flex items-center justify-between gap-2 text-[11.5px] leading-4 text-brand-muted">
          <span>{t('reader.list.count', { count: articles.length })}</span>
          <span className="truncate">{syncLabel}</span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto overscroll-contain px-2 pb-2">
        {loading ? (
          <ul>
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="my-0.5 flex flex-col gap-2 rounded-[10px] px-3 py-2.5">
                <div className="h-3 w-3/4 rounded bg-brand-border-medium/60" />
                <div className="h-2.5 w-1/2 rounded bg-brand-border-medium/40" />
                <div className="h-2.5 w-full rounded bg-brand-border-medium/40" />
              </li>
            ))}
          </ul>
        ) : error ? (
          <p className="px-3 py-6 text-center text-[13px] leading-5 text-brand-muted">
            {t('reader.list.error')}
          </p>
        ) : articles.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] leading-5 text-brand-muted">
            {t('reader.list.empty')}
          </p>
        ) : (
          <ul>
            {articles.map((article) => {
              const selected = article.id === selectedArticleId;
              const excerpt = textFromHtml(article.excerpt);
              return (
                <li key={article.id}>
                  <button
                    type="button"
                    onClick={() => {
                      track('output_article_selected', {
                        feed_id: feedId,
                        has_summary: hasSummary(article),
                        source_domain: hostnameOf(article.source_url || article.url),
                      });
                      onSelectArticle(article);
                    }}
                    className={cn(
                      'my-0.5 flex w-full cursor-pointer flex-col items-start gap-1 rounded-[10px] border-0 px-3 py-2.5 text-left transition-colors',
                      selected
                        ? 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
                        : 'bg-transparent hover:bg-white/60',
                    )}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <span
                      className={cn(
                        'line-clamp-2 text-[13.5px] leading-[1.35] tracking-[-0.01em] text-brand-text',
                        selected ? 'font-semibold' : 'font-medium',
                      )}
                    >
                      {article.title}
                    </span>
                    <span className="text-[11.5px] leading-4 text-brand-muted">
                      {hostnameOf(article.source_url || article.url)}
                      {article.published_at
                        ? ` · ${formatRelative(article.published_at, t)}`
                        : ''}
                    </span>
                    {excerpt && (
                      <span className="line-clamp-2 text-[12px] leading-[1.4] text-brand-subtle">
                        {excerpt}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
